import {Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';

import {getSsoSessionToken} from 'frontend/interceptors/auth_interceptor';
import {decodeJwt} from 'frontend/utils/auth_helpers';

/**
 * Interface representing the structure of identity information.
 */
export interface GcpIapIdentity {
  cookie_name?: string;
  email?: string;
  gcp_resource_id?: string;
  gcp_resource_project_number?: number;
  gcp_resource_type?: string;
  sub?: string;
}

/**
 * Service for resolving user identity information from the SSO session token cookie.
 */
@Injectable({
  providedIn: 'root',
})
export class IdentityService {
  /**
   * Fetches the identity of the current user from the SSO session token cookie.
   *
   * @returns An Observable that emits the user's identity, or null if it cannot be resolved.
   */
  fetchGcpIapIdentity(): Observable<GcpIapIdentity | null> {
    return of(this.getIdentityFromCookie());
  }

  /**
   * Resolves the identity from the SSO session token cookie.
   * Decodes the JWT token to extract the user's email.
   *
   * The session token cookie name is resolved from the host currently being served,
   * so each environment reads its own cookie. This keeps sessions isolated across
   * environments that share the apex domain.
   *
   * @returns The resolved identity, or null if the cookie is missing or invalid.
   */
  private getIdentityFromCookie(): GcpIapIdentity | null {
    const token = getSsoSessionToken();
    if (!token) {
      return null;
    }
    const decoded = decodeJwt(token);
    if (!decoded || typeof decoded.email !== 'string') {
      return null;
    }
    return {
      email: decoded.email,
    };
  }
}
